import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import emailjs from '@emailjs/browser'

// EmailJS config
const EMAILJS_SERVICE_ID          = 'service_vj4qazr'
const EMAILJS_PUBLIC_KEY          = 'B79ZWatUsgp1uHs70'
const EMAILJS_TEMPLATE_ID         = 'template_4342adu'

type FormData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  dateOfBirth: string
  course: string
  preferredSchedule: string
  message?: string
}

const packageToValue: Record<string, string> = {
  '5-Hour Package':        '5-hour',
  '7-Hour Package':        '7-hour',
  '10-Hour Package':       '10-hour',
  'Per Hour':              'per-hour',
  'Team Behind the Wheel': 'team-behind-wheel',
}

const courseLabels: Record<string, string> = {
  'per-hour':          'Per Hour - $65/hr',
  '5-hour':            '5-Hour Package - $299',
  '7-hour':            '7-Hour Package - $399',
  '10-hour':           '10-Hour Package - $599',
  'team-behind-wheel': 'Team Behind the Wheel - $359',
}

const scheduleLabels: Record<string, string> = {
  'weekday-morning':   'Weekday Morning (9:00 AM - 12:00 PM)',
  'weekday-afternoon': 'Weekday Afternoon (12:00 PM - 6:00 PM)',
  'weekend-morning':   'Weekend Morning (9:00 AM - 12:00 PM)',
  'weekend-afternoon': 'Weekend Afternoon (12:00 PM - 4:00 PM)',
  'flexible':          'Flexible',
}

export default function RegisterForm() {
  const [searchParams] = useSearchParams()
  const preselectedPackage = searchParams.get('package') ?? ''
  const defaultCourse = packageToValue[preselectedPackage] ?? ''

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ defaultValues: { course: defaultCourse } })

  const onSubmit = async (data: FormData) => {
    console.log('onSubmit fired', data)
    setSubmitError('')

    const courseName   = courseLabels[data.course] ?? data.course
    const scheduleName = scheduleLabels[data.preferredSchedule] ?? data.preferredSchedule
    const fullName     = `${data.firstName} ${data.lastName}`

    try {
      const [adminResult, confirmResult] = await Promise.allSettled([
        // 1. Notify admin
        emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            from_name:     fullName,
            from_email:    data.email,
            phone:         data.phone,
            date_of_birth: data.dateOfBirth,
            course:        courseName,
            schedule:      scheduleName,
            message:       data.message || 'N/A',
            reply_to:      data.email,
          },
          EMAILJS_PUBLIC_KEY
        ),
        // 2. Confirm to user
        emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            user_name:  fullName,
            user_email: data.email,
            course:     courseName,
            schedule:   scheduleName,
            message:    data.message || 'N/A',
          },
          EMAILJS_PUBLIC_KEY
        ),
      ])

      console.log('Admin email result:       ', adminResult.status, adminResult)
      console.log('Confirmation email result:', confirmResult.status, confirmResult)

      if (adminResult.status === 'rejected') {
        const err = (adminResult as PromiseRejectedResult).reason
        console.error('Admin email failed — status:', err?.status, 'text:', err?.text)
        throw err
      }

      if (confirmResult.status === 'rejected') {
        const err = (confirmResult as PromiseRejectedResult).reason
        console.error('Confirmation email failed — status:', err?.status, 'text:', err?.text)
        // Don't block success — admin registration still went through
      }

      setIsSubmitted(true)
    } catch (error: any) {
      console.error('Submit failed:', error)
      setSubmitError(`Error ${error?.status ?? ''}: ${error?.text ?? 'Unknown error'}. Please call us directly.`)
    }
  }

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white p-8 rounded-lg shadow-soft text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
        >
          <CheckCircleIcon className="w-16 h-16 text-success mx-auto mb-4" />
        </motion.div>
        <h2 className="text-2xl font-heading font-bold text-primary mb-2">
          Registration Successful!
        </h2>
        <p className="text-secondary-text mb-6">
          Thank you for registering. We&apos;ll contact you within 24 hours to confirm your enrollment and schedule.
        </p>
        <a
          href="tel:540-615-2418"
          className="inline-block px-6 py-3 bg-accent text-primary font-heading font-semibold rounded-full hover:scale-105 transition-transform"
        >
          Call Us: 540-615-2418
        </a>
      </motion.div>
    )
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit(onSubmit, (validationErrors) => {
        console.log('Validation errors — form not submitted:', validationErrors)
      })}
      className="bg-white p-8 rounded-lg shadow-soft"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="firstName" className="block text-sm font-heading font-semibold text-primary mb-2">
            First Name <span className="text-error">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            {...register('firstName', { required: 'First name is required' })}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-invalid={errors.firstName ? 'true' : 'false'}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-error">{errors.firstName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-heading font-semibold text-primary mb-2">
            Last Name <span className="text-error">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            {...register('lastName', { required: 'Last name is required' })}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-invalid={errors.lastName ? 'true' : 'false'}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-error">{errors.lastName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-heading font-semibold text-primary mb-2">
            Email Address <span className="text-error">*</span>
          </label>
          <input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-invalid={errors.email ? 'true' : 'false'}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-error">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-heading font-semibold text-primary mb-2">
            Phone Number <span className="text-error">*</span>
          </label>
          <input
            id="phone"
            type="tel"
            {...register('phone', {
              required: 'Phone number is required',
              pattern: {
                value: /^[\d\s\-()]+$/,
                message: 'Invalid phone number',
              },
            })}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-invalid={errors.phone ? 'true' : 'false'}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-error">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="dateOfBirth" className="block text-sm font-heading font-semibold text-primary mb-2">
            Date of Birth <span className="text-error">*</span>
          </label>
          <input
            id="dateOfBirth"
            type="date"
            {...register('dateOfBirth', { required: 'Date of birth is required' })}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-invalid={errors.dateOfBirth ? 'true' : 'false'}
          />
          {errors.dateOfBirth && (
            <p className="mt-1 text-sm text-error">{errors.dateOfBirth.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="course" className="block text-sm font-heading font-semibold text-primary mb-2">
            Course Selection <span className="text-error">*</span>
          </label>
          <select
            id="course"
            {...register('course', { required: 'Please select a course' })}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-invalid={errors.course ? 'true' : 'false'}
          >
            <option value="">Select a course...</option>
            <option value="per-hour">Per Hour - $65/hr</option>
            <option value="5-hour">5-Hour Package - $299</option>
            <option value="7-hour">7-Hour Package - $399</option>
            <option value="10-hour">10-Hour Package - $599</option>
            <option value="team-behind-wheel">Team Behind the Wheel - $359</option>
          </select>
          {errors.course && (
            <p className="mt-1 text-sm text-error">{errors.course.message}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label htmlFor="preferredSchedule" className="block text-sm font-heading font-semibold text-primary mb-2">
            Preferred Schedule <span className="text-error">*</span>
          </label>
          <select
            id="preferredSchedule"
            {...register('preferredSchedule', { required: 'Please select a schedule preference' })}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-invalid={errors.preferredSchedule ? 'true' : 'false'}
          >
            <option value="">Select schedule preference...</option>
            <option value="weekday-morning">Weekday Morning (9:00 AM - 12:00 PM)</option>
            <option value="weekday-afternoon">Weekday Afternoon (12:00 PM - 6:00 PM)</option>
            <option value="weekend-morning">Weekend Morning (9:00 AM - 12:00 PM)</option>
            <option value="weekend-afternoon">Weekend Afternoon (12:00 PM - 4:00 PM)</option>
            <option value="flexible">Flexible</option>
          </select>
          {errors.preferredSchedule && (
            <p className="mt-1 text-sm text-error">{errors.preferredSchedule.message}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label htmlFor="message" className="block text-sm font-heading font-semibold text-primary mb-2">
            Additional Message (Optional)
          </label>
          <textarea
            id="message"
            rows={4}
            {...register('message')}
            className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Any special requests or questions?"
          />
        </div>
      </div>

      {submitError && (
        <p className="mb-4 text-sm text-error text-center">{submitError}</p>
      )}

      <motion.button
        type="submit"
        disabled={isSubmitting}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="w-full px-8 py-4 bg-accent text-primary font-heading font-semibold rounded-full hover:shadow-lift transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Registration'}
      </motion.button>
    </motion.form>
  )
}
